import { useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Avatar, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { useQueryClient } from '@tanstack/react-query';
import Truncate from 'components/Truncate';
import { EDUZZ_ACCOUNT_URL, VERTICAL_URL } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import type { IAccess } from 'v2/interfaces/account';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Layout from '@eduzz/ui-layout';

const { Topbar } = Layout;

const ProfileMenu = () => {
  const [isLoading] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []) ?? [];

  const queryClient = useQueryClient();

  const handleClickLogout = useCallback(() => eduzzAccountsService.logout(), []);

  const handleClickChangeAccount = useCallback((access: IAccess) => {
    eduzzAccountsService.changeAccount(access.email);
  }, []);

  useEffect(() => {
    queryClient.invalidateQueries();
  }, [user?.id, queryClient]);

  if (!user) {
    return null;
  }

  const userHasRemoveNewBankAccountsBeta = eduzzAccountsService.verifyUserHasBetaModule('remove_new_bank_accounts');

  return (
    <Topbar.UserMenu id='topbar-user-menu'>
      <PermissionHide>
        {user.general_term && (
          <Topbar.UserMenuItem
            id='topbar-user-menu-item-bank-data'
            icon={<Icon.Wallet  />}
            {...(!userHasRemoveNewBankAccountsBeta
              ? {
                  as: 'a',
                  href: `${VERTICAL_URL}/bank-accounts/`
                }
              : {
                  as: NavLink,
                  to: '/producer/profile/bankdata'
                })}
          >
            Contas Bancárias
          </Topbar.UserMenuItem>
        )}

        <Topbar.UserMenuItem
          id='topbar-user-menu-item-profile-data'
          as='a'
          href={`${EDUZZ_ACCOUNT_URL}/profile`}
          icon={<Icon.IdCard  />}
          target='_blank'
          rel='noopener noreferrer'
        >
          Dados Cadastrais
        </Topbar.UserMenuItem>

        <Topbar.UserMenuItem
          id='topbar-user-menu-security'
          as='a'
          href={`${EDUZZ_ACCOUNT_URL}/security`}
          icon={<Icon.Lock  />}
          target='_blank'
          rel='noopener noreferrer'
        >
          Senha e Segurança
        </Topbar.UserMenuItem>

        <Topbar.UserMenuItem
          id='topbar-user-menu-item-payment-methods'
          as='a'
          href={`${VERTICAL_URL}/subscriptions/cards/me`}
          icon={<Icon.CreditCard  />}
        >
          Meus Cartões
        </Topbar.UserMenuItem>

        <Topbar.UserMenuDivider />
      </PermissionHide>

      {user.general_term && (
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-purchases'
          as={NavLink}
          to='/producer/shopping'
          icon={<Icon.ShoppingCart  />}
        >
          Minhas Compras
        </Topbar.UserMenuItem>
      )}

      {user.general_term && (
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-my-subscriptions'
          as='a'
          href={`${VERTICAL_URL}/subscriptions/me`}
          icon={<Icon.RefreshCw  />}
        >
          Minhas Assinaturas
        </Topbar.UserMenuItem>
      )}

      {user.acceptence_terms && (
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-dashboard-elite'
          as='a'
          href={`${VERTICAL_URL}/dashboard-elite`}
          icon={<Icon.Star  />}
        >
          Dashboard Elite
        </Topbar.UserMenuItem>
      )}

      {!!user.is_clube_black && (
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-unity'
          as={NavLink}
          to={{ pathname: 'https://unity.eduzzlabs.com/' }}
          target='_blank'
          rel='noopener noreferrer'
          icon={<Icon.Shield  />}
        >
          Unity
        </Topbar.UserMenuItem>
      )}

      {accesses?.length > 1 && (
        <>
          <Topbar.UserMenuDivider />

          <Topbar.UserMenuGroup label='Contas:'>
            {accesses.map((access, index) => {
              const disableTooltip = access.termsAcceptance || access.isMe;
              const name = access?.name?.trim()?.substring(0, 1);

              return (
                <Tooltip
                  arrow
                  title={
                    disableTooltip ? (
                      ''
                    ) : (
                      <Typography.Text className='text-inherit'>
                        O titular desta conta ainda não aceitou a atualização dos nossos Termos de Uso. Solicite a
                        confirmação para o responsável e assim que ele a fizer o seu acesso será liberado. Em caso de
                        dúvidas entre em contato com o nosso suporte: suporte@eduzz.com
                      </Typography.Text>
                    )
                  }
                  placement='left'
                  key={access.id}
                >
                  <Topbar.UserMenuItem
                    id={`top-user-menu-item-account-${index}`}
                    onClick={() => handleClickChangeAccount(access)}
                    disabled={isLoading || (!access.termsAcceptance && !access.isMe)}
                    icon={
                      <Avatar size={28} src={access?.photo}>
                        {name}
                      </Avatar>
                    }
                  >
                    <Truncate count={25} final='...'>
                      {access?.name}
                    </Truncate>
                  </Topbar.UserMenuItem>
                </Tooltip>
              );
            })}
          </Topbar.UserMenuGroup>
        </>
      )}

      <Topbar.UserMenuDivider />

      <PermissionHide action='myeduzz:ajuda:visualizar'>
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-help'
          as={NavLink}
          to='/producer/help'
          icon={<Icon.CircleHelp  />}
        >
          Ajuda
        </Topbar.UserMenuItem>
      </PermissionHide>

      <Topbar.UserMenuItem id='topbar-user-menu-item-exit' icon={<Icon.LogOut  />} onClick={handleClickLogout}>
        Sair
      </Topbar.UserMenuItem>
    </Topbar.UserMenu>
  );
};

export default ProfileMenu;
